'use client';

import { useEffect, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  formatMarkdownTable,
  generateMarkdownTable,
  renderMarkdownToHtml,
  type TableAlignment,
} from '@/lib/browser-markdown-tools';

type Variant = 'markdown-table-generator' | 'markdown-table-formatter';

const generatorExample = `Feature,Audience,Status
Markdown preview,Docs writers,Ready
HTML conversion,Founders,Planned`;

const formatterExample = `|Feature|Audience|Status|
|:--|--:|:--:|
|Markdown preview|Docs writers|Ready|
|HTML conversion|Founders|Planned|`;

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    placeholder: string;
  }
> = {
  'markdown-table-generator': {
    title: 'Generate Markdown tables from grid-like text',
    description:
      'Paste comma, pipe, or tab-separated rows, choose the table shape, and export one clean Markdown table with GFM preview.',
    badge: 'Tables',
    actionLabel: 'Generate table',
    successLabel: 'Markdown table generation',
    placeholder: generatorExample,
  },
  'markdown-table-formatter': {
    title: 'Format messy Markdown tables into aligned output',
    description:
      'Paste an existing Markdown table, normalize the separator row, align the columns, and export cleaner Markdown locally.',
    badge: 'Format',
    actionLabel: 'Format table',
    successLabel: 'Markdown table formatting',
    placeholder: formatterExample,
  },
};

function createTextDownload(text: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

export default function BrowserMarkdownTableTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [inputValue, setInputValue] = useState('');
  const [columnCount, setColumnCount] = useState(3);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [alignments, setAlignments] = useState<TableAlignment[]>(['left', 'left', 'left']);
  const [resultMarkdown, setResultMarkdown] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    setAlignments((current) => {
      const next = [...current.slice(0, columnCount)];
      while (next.length < columnCount) {
        next.push('left');
      }
      return next;
    });
  }, [columnCount]);

  function resetOutput() {
    setResultMarkdown(null);
    setPreviewHtml(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function runTool() {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setErrorMessage('Paste some table content first.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another Markdown table tool run right now.',
      );
      const markdown =
        variant === 'markdown-table-generator'
          ? generateMarkdownTable(inputValue, columnCount, hasHeaderRow, alignments)
          : formatMarkdownTable(inputValue);
      const html = await renderMarkdownToHtml(markdown);
      setResultMarkdown(markdown);
      setPreviewHtml(html);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResultMarkdown(null);
      setPreviewHtml(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This Markdown table task could not be completed.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy() {
    if (!resultMarkdown) {
      return;
    }

    try {
      await copyText(resultMarkdown);
      setCopyMessage('Markdown table copied.');
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current Markdown table.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>{variant === 'markdown-table-generator' ? 'Rows' : 'Markdown table'}</span>
              <textarea
                onChange={(event) => {
                  resetOutput();
                  setInputValue(event.target.value);
                }}
                placeholder={copy.placeholder}
                value={inputValue}
              />
            </label>

            {variant === 'markdown-table-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Columns</span>
                  <input
                    max={8}
                    min={1}
                    onChange={(event) => {
                      resetOutput();
                      setColumnCount(Math.min(8, Math.max(1, Number(event.target.value) || 1)));
                    }}
                    type="number"
                    value={columnCount}
                  />
                </label>

                <label className="tool-live-checkbox">
                  <input
                    checked={hasHeaderRow}
                    onChange={(event) => {
                      resetOutput();
                      setHasHeaderRow(event.target.checked);
                    }}
                    type="checkbox"
                  />
                  <span>First row is already the header</span>
                </label>

                <div className="tool-markdown-alignment-grid tool-live-field-full">
                  {alignments.map((alignment, index) => (
                    <label className="tool-live-field" key={`alignment-${index}`}>
                      <span>Column {index + 1}</span>
                      <select
                        onChange={(event) => {
                          resetOutput();
                          const next = [...alignments];
                          next[index] = event.target.value as TableAlignment;
                          setAlignments(next);
                        }}
                        value={alignment}
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void runTool()}
              type="button"
            >
              {isRunning ? 'Working…' : copy.actionLabel}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {resultMarkdown ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy()}
                type="button"
              >
                Copy table
              </button>
              <button
                className="button button-secondary"
                onClick={() => createTextDownload(resultMarkdown, 'table.md')}
                type="button"
              >
                Download .md
              </button>
            </div>
          ) : null}

          {resultMarkdown ? (
            <div className="tool-live-preview">
              <h3>Markdown output</h3>
              <pre className="tool-markdown-code">{resultMarkdown}</pre>
            </div>
          ) : null}

          {previewHtml ? (
            <div className="tool-live-preview">
              <h3>Rendered table</h3>
              <div
                className="tool-markdown-rendered"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
